/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Chart } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
  STATE_STATUS,
  DASHBOARD_COMPLETENESS_COLOURS,
  DASHBOARD_COMPLETENESS_DESCRIPTIONS,
  DASHBOARD_DOUGHNUT_TYPES,
  DASHBOARD_QUALITY_COLOURS,
  DASHBOARD_QUALITY_DESCRIPTIONS,
  DASHBOARD_TIMELINE_COLOURS,
  DASHBOARD_TIMELINE_DESCRIPTIONS,
} from '@awp/constants';

import { isValueEmpty, isInt, isMatchArray } from '@awp/helpers/utilsReact';
import { currentDashboardViews } from '@stores/dashboardViewPageSlice';
import AwpSpinner from '@components/Common/AwpSpinner';

const DEFAULT_VALUE = -999;

const DoughnutInnerText = {
  id: 'doughnutInnerText',
  beforeDraw: (chart) => {
    if (!chart.options.centerText) {
      return;
    }
    const chartArea = chart.chartArea;
    const top = chartArea.top;
    const left = chartArea.left;
    const height = chartArea.height;
    const width = chartArea.width;
    const centerText = chart.options.centerText;
    const ctx = chart.ctx;

    let fontSize = (height / 250).toFixed(2);
    if (centerText.fontSizeAdjust) {
      fontSize = fontSize * (1 + centerText.fontSizeAdjust);
    }
    ctx.font = fontSize + 'em sans-serif';
    ctx.textBaseline = 'middle';
    const textX = Math.round((width - ctx.measureText(centerText.value).width) / 2 + left);

    const textY = Math.round(height / 2 + top);
    if (centerText.color) {
      ctx.fillStyle = centerText.color;
    }
    ctx.fillText(centerText.value, textX, textY);
  },
};
Chart.register(DoughnutInnerText);

const Graph = ({ graphId, insideText, dashboardType }) => {
  const dashboardViews = useSelector(currentDashboardViews);
  const graphViewsStatus = useSelector((state) => state.dashboardViewPage.status);
  const [graphData, setGraphData] = useState(null);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    if (!dashboardViews) return;
    let apiData = null;
    let _backgroundColor = DASHBOARD_COMPLETENESS_COLOURS;
    let _data_timeliness = [DEFAULT_VALUE, 0]; //Green // Yellow
    let _data_completeness = [0, 0, 0, DEFAULT_VALUE]; // Green // Purple // Red // Grey
    let _data_quality = [DEFAULT_VALUE, 0]; // Green // Red
    if (DASHBOARD_DOUGHNUT_TYPES.COMPLETENESS == dashboardType) {
      if (
        !isValueEmpty(dashboardViews.CompletenessStats) &&
        !isMatchArray(dashboardViews.CompletenessStats, [0, 0, 0, 0])
      ) {
        let ngreen = dashboardViews.CompletenessStats[0];
        let nred = dashboardViews.CompletenessStats[2];
        let total = ngreen + nred;
        if (total === 0) {
          apiData = _data_completeness;
          setNoData(true);
        } else {
          apiData = [ngreen, 0, nred, 0];
          setNoData(false);
        }
      } else {
        apiData = _data_completeness;
        setNoData(true);
      }
    } else if (DASHBOARD_DOUGHNUT_TYPES.TIMELINESS == dashboardType) {
      if (
        !isValueEmpty(dashboardViews.TimelinessStats) &&
        dashboardViews.TimelinessStats.CountLate + dashboardViews.TimelinessStats.CountOnTime !== 0
      ) {
        let ontime = isInt(dashboardViews.TimelinessStats.CountOnTime)
          ? Number(dashboardViews.TimelinessStats.CountOnTime)
          : 0;
        let late = isInt(dashboardViews.TimelinessStats.CountLate)
          ? Number(dashboardViews.TimelinessStats.CountLate)
          : 0;
        let total = ontime + late;
        if (total === 0) {
          apiData = _data_timeliness;
          setNoData(true);
        } else {
          let ontimePercent = Math.round((ontime / total) * 10000.0) / 100;
          let latePercent = Math.round((late / total) * 10000.0) / 100;
          apiData = [ontimePercent, latePercent];
          setNoData(false);
        }
      } else {
        apiData = _data_timeliness;
        setNoData(true);
      }
      _backgroundColor = DASHBOARD_TIMELINE_COLOURS;
    } else if (DASHBOARD_DOUGHNUT_TYPES.QUALITY == dashboardType) {
      if (
        !isValueEmpty(dashboardViews.QualityStats) &&
        dashboardViews.QualityStats.CountInvalid + dashboardViews.QualityStats.CountValid !== 0
      ) {
        let valid = isInt(dashboardViews.QualityStats.CountValid) ? Number(dashboardViews.QualityStats.CountValid) : 0;
        let invalid = isInt(dashboardViews.QualityStats.CountInvalid)
          ? Number(dashboardViews.QualityStats.CountInvalid)
          : 0;
        let total = invalid + valid;
        if (total === 0) {
          apiData = _data_quality;
          setNoData(true);
        } else {
          let invalidPercent = ((invalid / valid) * 100).toFixed(2);
          //Math.round((valid / total) * 10000.0) / 100;
          let validPercent = (100 - invalidPercent).toFixed(2);
          //Math.round((invalid / total) * 10000.0) / 100;
          apiData = [validPercent, invalidPercent];
          setNoData(false);
        }
      } else {
        apiData = _data_quality;
        setNoData(true);
      }
      _backgroundColor = DASHBOARD_QUALITY_COLOURS;
    }
    let _data = apiData; // Green // Purple // Red // Grey
    setGraphData({
      datasets: [
        {
          label: graphId,
          data: _data, // Green // Purple // Red // Grey
          backgroundColor: _backgroundColor,
          borderWidth: 1,
        },
      ],
    });
  }, [dashboardViews, dashboardType]);

  const options = {
    centerText: {
      color: '#000',
      value: insideText,
      //fontSizeAdjust: 0.2 // increase font size 20% based on default font size
    },
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            let currentValue = tooltipItem.dataset.data[tooltipItem.dataIndex];
            let currentDescriptions = DASHBOARD_COMPLETENESS_DESCRIPTIONS;
            if (DASHBOARD_DOUGHNUT_TYPES.COMPLETENESS == dashboardType) {
              currentDescriptions = DASHBOARD_COMPLETENESS_DESCRIPTIONS;
            } else if (DASHBOARD_DOUGHNUT_TYPES.QUALITY == dashboardType) {
              currentDescriptions = DASHBOARD_QUALITY_DESCRIPTIONS;
            } else if (DASHBOARD_DOUGHNUT_TYPES.TIMELINESS == dashboardType) {
              currentDescriptions = DASHBOARD_TIMELINE_DESCRIPTIONS;
            }
            if ((currentValue + '').trim() === DEFAULT_VALUE + '') currentValue = 0;
            return currentValue + ' ' + currentDescriptions[tooltipItem.dataIndex];
          },
        },
      },
      legend: {
        position: 'top',
        align: 'center',
      },
      title: {
        display: false,
      },
    },
  };
  return (
    <>
      <div>
        {graphViewsStatus == STATE_STATUS.LOADING && <AwpSpinner />}
        {graphViewsStatus == STATE_STATUS.SUCCESS && graphData && (
          <Doughnut id={graphId} type="doughnut" key={graphId} options={options} data={graphData} />
        )}
      </div>
      {graphViewsStatus == STATE_STATUS.SUCCESS && noData && (
        <div className="text-center">No weather station in this area</div>
      )}
    </>
  );
};

Graph.propTypes = {
  graphId: PropTypes.any.isRequired,
  presentation: PropTypes.string,
  dashboardType: PropTypes.any.isRequired,
};

export default Graph;
